<script setup lang="ts">
import { useI18n } from '@/i18n';
import type { Subscription } from '@/types/billing';
import { Alert, Button, LayoutGap } from '@dabble/quillui';
import { computed } from 'vue';

const props = defineProps<{
  subscription?: Subscription | null;
  disabled?: boolean;
  // The past-due invoice is already being paid by a debit that is still clearing (DAB-1690).
  paymentProcessing?: boolean;
}>();

const emit = defineEmits<{ (e: 'undoCancellation'): void }>();

const { t } = useI18n();

// The legacy pause system parked the subscription on a trial whose trial_end is the resume
// date, so a paused-the-old-way sub reports status 'trialing' while carrying
// metadata.is_paused_until. It is NOT a real trial, and the exclusion below must not swallow
// its cancellation notice.
//
// Deliberately narrower than isPaused()/isSubscriptionPaused elsewhere: this only needs the
// legacy marker. A natively paused sub (pause_collection) or Stripe's own `paused` status never
// reports `trialing`, so the exclusion this guards cannot fire for them and they already reach
// the warning below.
function isLegacyPaused(sub: Subscription): boolean {
  const until = sub.metadata?.is_paused_until;
  return until != null && until !== '';
}

const messageType = computed<'danger' | 'warning' | 'info' | null>(() => {
  const sub = props.subscription;
  if (sub?.status === 'past_due') {
    return props.paymentProcessing ? 'info' : 'danger';
  }
  // On a genuine trial, Stripe sets cancel_at_period_end automatically when there is
  // no payment method — the trial just ends naturally, so there is nothing for the user
  // to "undo". A legacy-paused sub only looks trialing, so it keeps its notice: DAB-922's
  // reporter cancelled successfully and this banner — the one surface that would have told
  // her so — stayed hidden because her pause read as a trial.
  const isRealTrial = sub?.status === 'trialing' && !isLegacyPaused(sub);
  if (sub?.cancel_at_period_end && !isRealTrial) {
    return 'warning';
  }
  return null;
});

const message = computed(() => {
  if (messageType.value === 'info') {
    return t(
      'billing_warning_payment_processing',
      "Your bank payment is on its way. Bank payments take a few business days to clear, so there's nothing more you need to do.",
    );
  }
  if (messageType.value === 'danger') {
    return t('billing_warning_unpaid', 'Your payment is past due. Please update your payment method.');
  }
  if (messageType.value === 'warning') {
    return t('billing_warning_canceled', 'Your subscription is scheduled to cancel at the end of the period.');
  }
  return null;
});

const showUndoButton = computed(() => messageType.value === 'warning');
</script>

<template>
  <Alert v-if="messageType && message" :variant="messageType" class="billing-subscription-alert">
    <LayoutGap direction="row" size="small" vertical-align="center" wrap>
      <span>{{ message }}</span>
      <Button
        v-if="showUndoButton"
        finish="link"
        variant="secondary"
        icon-left="undo"
        class="subscription-message__undo"
        :disabled="disabled"
        @click="emit('undoCancellation')"
      >
        {{ t('billing_cancellation_undo', 'Undo Cancellation') }}
      </Button>
    </LayoutGap>
  </Alert>
</template>

<style lang="scss" scoped>
.billing-subscription-alert {
  margin-top: 1rem;
}

.subscription-message__undo {
  color: var(--color-fg-warning);
  border-color: var(--color-border-warning);

  &:hover:not(:disabled) {
    color: var(--color-fg-warning-hover);
    background-color: var(--color-bg-warning);
  }
}
</style>
